import axios from 'axios';
import { 
  Client, 
  Product, 
  Ranking, 
  CreateClientRequest, 
  UpdateClientRequest, 
  CreateProductRequest, 
  UpdateProductRequest,
  RankingFilter,
  ProductCompetitor,
  CreateProductCompetitorRequest,
  UpdateProductCompetitorRequest,
  KeywordTag,
  CreateKeywordTagRequest,
  UpdateKeywordTagRequest
} from '../types';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:18000';

const api = {
  // Client endpoints
  getClients: async (): Promise<Client[]> => {
    const response = await axios.get(`${API_URL}/api/clients/`);
    return response.data;
  },

  getClient: async (clientId: number): Promise<Client> => {
    const response = await axios.get(`${API_URL}/api/clients/${clientId}`);
    return response.data;
  },

  createClient: async (data: CreateClientRequest): Promise<Client> => {
    const response = await axios.post(`${API_URL}/api/clients/`, data);
    return response.data;
  },

  updateClient: async (clientId: number, data: UpdateClientRequest): Promise<Client> => {
    const response = await axios.put(`${API_URL}/api/clients/${clientId}`, data);
    return response.data;
  },

  deleteClient: async (clientId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/clients/${clientId}`);
  },

  bulkCreateClients: async (names: string[]): Promise<{
    message: string;
    created_count: number;
    skipped_count: number;
    created_clients: string[];
    skipped_clients: string[];
  }> => {
    const response = await axios.post(`${API_URL}/api/clients/bulk-create`, { names });
    return response.data;
  },

  // Product endpoints
  getProducts: async (clientId: number): Promise<Product[]> => {
    const response = await axios.get(`${API_URL}/api/clients/${clientId}/products/`);
    return response.data;
  },

  getProduct: async (clientId: number, productId: number): Promise<Product> => {
    const response = await axios.get(`${API_URL}/api/clients/${clientId}/products/${productId}`);
    return response.data;
  },

  createProduct: async (clientId: number, data: CreateProductRequest): Promise<Product> => {
    const response = await axios.post(`${API_URL}/api/clients/${clientId}/products/`, data);
    return response.data;
  },

  updateProduct: async (clientId: number, productId: number, data: UpdateProductRequest): Promise<Product> => {
    const response = await axios.put(`${API_URL}/api/clients/${clientId}/products/${productId}`, data);
    return response.data;
  },

  deleteProduct: async (clientId: number, productId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/products/${productId}`);
  },

  // ASIN endpoints
  addProductAsin: async (productId: number, asin: string): Promise<any> => {
    const response = await axios.post(`${API_URL}/api/products/${productId}/asins/`, { asin });
    return response.data;
  },

  deleteProductAsin: async (productId: number, asinId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/products/${productId}/asins/${asinId}`);
  },

  // Product Competitor endpoints
  getProductCompetitors: async (productId: number): Promise<ProductCompetitor[]> => {
    const response = await axios.get(`${API_URL}/api/products/${productId}/competitors/`);
    return response.data;
  },

  getProductCompetitorCount: async (productId: number): Promise<number> => {
    const response = await axios.get(`${API_URL}/api/products/${productId}/competitors/`);
    return response.data.length;
  },

  getProductCompetitor: async (productId: number, competitorId: number): Promise<ProductCompetitor> => {
    const response = await axios.get(`${API_URL}/api/products/${productId}/competitors/${competitorId}`);
    return response.data;
  },

  createProductCompetitor: async (productId: number, data: CreateProductCompetitorRequest): Promise<ProductCompetitor> => {
    const response = await axios.post(`${API_URL}/api/products/${productId}/competitors/`, data);
    return response.data;
  },

  updateProductCompetitor: async (productId: number, competitorId: number, data: UpdateProductCompetitorRequest): Promise<ProductCompetitor> => {
    const response = await axios.put(`${API_URL}/api/products/${productId}/competitors/${competitorId}`, data);
    return response.data;
  },

  deleteProductCompetitor: async (productId: number, competitorId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/products/${productId}/competitors/${competitorId}`);
  },

  // Competitor Analysis endpoint
  getCompetitorAnalysis: async (productId: number, params: any): Promise<any> => {
    const response = await axios.get(`${API_URL}/api/products/${productId}/competitor-analysis`, { params });
    return response.data;
  },

  // Rankings endpoints
  getRankings: async (filter: RankingFilter): Promise<Ranking[]> => {
    const params = {
      client_id: filter.client_id,
      product_ids: filter.product_ids,  
      days: filter.days,
      min_search_volume: filter.min_search_volume,
      max_position: filter.max_position,
      market: filter.market,
      custom_asins: filter.custom_asins
    };
    const response = await axios.get(`${API_URL}/api/rankings/`, { params });
    return response.data;
  },

  // Keyword Tag endpoints
  getProductKeywordTags: async (productId: number): Promise<KeywordTag[]> => {
    const response = await axios.get(`${API_URL}/api/products/${productId}/tags`);
    return response.data;
  },

  getKeywordTag: async (tagId: number): Promise<KeywordTag> => {
    const response = await axios.get(`${API_URL}/api/tags/${tagId}`);
    return response.data;
  },

  createKeywordTag: async (productId: number, data: CreateKeywordTagRequest): Promise<KeywordTag> => {
    const response = await axios.post(`${API_URL}/api/products/${productId}/tags`, data);
    return response.data;
  },

  updateKeywordTag: async (tagId: number, data: UpdateKeywordTagRequest): Promise<KeywordTag> => {
    const response = await axios.put(`${API_URL}/api/tags/${tagId}`, data);
    return response.data;
  },

  deleteKeywordTag: async (tagId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/tags/${tagId}`);
  }
};

export default api;
